import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Card, message, Spin, Alert, Typography, Tag, Button, Space, Progress, Tooltip } from 'antd';
import { CopyOutlined, ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { IncomingStream, IncomingStreamsResponse, Server } from '../../types/server';
import { Stream } from '../../types/stream';
import { apiRequest } from '../../services/api';
import { serverService } from '../../services/serverService';

const { Title } = Typography;

const IncomingStreamsPage: React.FC = () => {
  const { t } = useTranslation();
  const [streams, setStreams] = useState<IncomingStream[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [liveStreams, setLiveStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Function to fetch all data
  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    try {
      // Fetch servers, incoming streams, and live streams in parallel
      const [serversResponse, streamsResponse, liveStreamsResponse] = await Promise.all([
        serverService.getAllServers(),
        apiRequest<IncomingStreamsResponse>('/servers/incoming'),
        apiRequest<{ streams: Stream[] }>('/streams')
      ]);

      setServers(serversResponse || []);
      setLiveStreams(liveStreamsResponse.streams || []);
      
      // Match servers with streams and add server_url
      const streamsWithServerUrl = (streamsResponse.streams || []).map(stream => {
        const server = serversResponse.find(s => s.server_id === stream.server_id);
        return {
          ...stream,
          server_url: server?.server_url || ''
        };
      });
      
      setStreams(streamsWithServerUrl);
    } catch (error) {
      console.error('Error fetching data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load data';
      setError(errorMessage);
      if (showLoading) {
        message.error(t('incoming.notifications.error', { message: errorMessage }));
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [t]);

  // Start countdown timer
  const startCountdown = useCallback(() => {
    setCountdown(5);
    countdownTimerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          return 5; // Reset countdown
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Stop countdown timer
  const stopCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  // Start refresh timer
  const startRefreshTimer = useCallback(() => {
    refreshTimerRef.current = setInterval(() => {
      fetchData(false); // Don't show loading spinner for background refresh
    }, 5000);
  }, [fetchData]);

  // Stop refresh timer
  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Toggle auto refresh
  const toggleAutoRefresh = useCallback(() => {
    setIsAutoRefreshing(prev => {
      const newValue = !prev;
      if (newValue) {
        startRefreshTimer();
        startCountdown();
      } else {
        stopRefreshTimer();
        stopCountdown();
      }
      return newValue;
    });
  }, [startRefreshTimer, stopRefreshTimer, startCountdown, stopCountdown]);

  // Load data on component mount and setup auto refresh
  useEffect(() => {
    fetchData();
    if (isAutoRefreshing) {
      startRefreshTimer();
      startCountdown();
    }

    // Cleanup on unmount
    return () => {
      stopRefreshTimer();
      stopCountdown();
    };
  }, [fetchData, isAutoRefreshing, startRefreshTimer, startCountdown, stopRefreshTimer, stopCountdown]);

  // Format bandwidth value
  const formatBandwidth = (bandwidth?: number): string => {
    if (bandwidth === undefined || bandwidth === null) {
      return '-';
    }
    const mbps = Math.ceil((bandwidth / 1000000) * 10) / 10;
    return `${mbps.toFixed(1)} Mb`;
  };

  // Construct stream URL
  const constructStreamUrl = (stream: IncomingStream): string => {
    if (stream.status !== 'online' || !stream.outgoing_streams || stream.outgoing_streams.length === 0) {
      return '-';
    }
    
    const outgoingStream = stream.outgoing_streams[0];
    return `${stream.server_url}/${outgoingStream.application}/${outgoingStream.stream}/playlist.m3u8`;
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'online':
        return 'green';
      case 'offline':
        return 'red';
      case 'paused':
        return 'orange';
      default:
        return 'default';
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success(t('incoming.notifications.copy_success'));
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      message.error(t('incoming.notifications.copy_error'));
    }
  };

    // Check if incoming stream has associated live streams
  const hasAssociatedLiveStreams = (incomingStream: IncomingStream): boolean => {
    console.log('Checking incoming stream:', {
      id: incomingStream.id,
      server_id: incomingStream.server_id,
      status: incomingStream.status
    });
    
    const associatedStreams = liveStreams.filter(stream => {
      console.log('Comparing with stream:', {
        streamId: stream.id,
        status: stream.status,
        is_live: stream.is_live,
        server_incoming_stream_id: stream.server_incoming_stream_id,
        stream_server_id: stream.server_id,
        incoming_server_id: incomingStream.server_id,
        serverMatches: stream.server_id === incomingStream.server_id,
        streamMatches: stream.server_incoming_stream_id === incomingStream.id,
        isLive: stream.status === 'live' || stream.is_live
      });
      
      return (stream.status === 'live' || stream.is_live) && 
             stream.server_incoming_stream_id === incomingStream.id &&
             stream.server_id === incomingStream.server_id;
    });
    
    console.log('Found associated live streams:', associatedStreams.length);
    return associatedStreams.length > 0;
  };

  // Handle stream action (pause/resume)
  const handleStreamAction = async (stream: IncomingStream, action: 'pause' | 'resume') => {
    const server = servers.find(s => s.server_id === stream.server_id);
    if (!server) {
      message.error(t('incoming.notifications.server_not_found'));
      return;
    }

    setActionLoading(stream.id);
    
    try {
      // Use our API to handle the pause/resume action
      const endpoint = `/servers/incoming/${stream.id}/${action}`;
      
      const response = await apiRequest<any>(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          server_id: stream.server_id
        })
      });

      message.success(t(`incoming.notifications.${action}_success`));
      
      // Wait 5 seconds before refreshing data
      setTimeout(() => {
        fetchData(false);
        setActionLoading(null);
      }, 5000);
      
    } catch (error) {
      console.error(`Error ${action}ing stream:`, error);
      const errorMessage = error instanceof Error ? error.message : `Failed to ${action} stream`;
      message.error(t('incoming.notifications.action_error', { message: errorMessage }));
      setActionLoading(null);
    }
  };

  // Table columns
  const columns = [
    {
      title: t('incoming.list.status'),
      dataIndex: 'status',
      key: 'status',
      sorter: (a: IncomingStream, b: IncomingStream) => {
        const statusA = a.status?.toLowerCase() || '';
        const statusB = b.status?.toLowerCase() || '';
        return statusA.localeCompare(statusB);
      },
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: t('incoming.list.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IncomingStream, b: IncomingStream) => {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: t('incoming.list.url'),
      key: 'url',
      render: (_: any, record: IncomingStream) => {
        const url = constructStreamUrl(record);
        return url === '-' ? url : (
          <Space>
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(url)}
              title={t('incoming.list.copy_url')}
            />
          </Space>
        );
      },
    },
    {
      title: t('incoming.list.bandwidth'),
      dataIndex: 'bandwidth',
      key: 'bandwidth',
      render: (bandwidth: number) => formatBandwidth(bandwidth),
    },
    {
      title: t('incoming.list.actions'),
      key: 'actions',
      render: (_: any, record: IncomingStream) => {
        const isLoading = actionLoading === record.id;
        const status = record.status.toLowerCase();
        const hasLiveStreams = hasAssociatedLiveStreams(record);
        
        if (status === 'online') {
          // Don't show pause button if there are associated live streams
          if (hasLiveStreams) {
            return (
              <Tooltip title={t('incoming.list.cannot_pause_live_streams')}>
                <Button
                  type="text"
                  size="small"
                  icon={<PauseCircleOutlined />}
                  disabled={true}
                  style={{ opacity: 0.3 }}
                />
              </Tooltip>
            );
          }
          
          return (
            <Button
              type="text"
              size="small"
              icon={<PauseCircleOutlined />}
              onClick={() => handleStreamAction(record, 'pause')}
              loading={isLoading}
              disabled={isLoading}
              title={t('incoming.list.pause_stream')}
            />
          );
        } else if (status === 'paused') {
          return (
            <Button
              type="text"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleStreamAction(record, 'resume')}
              loading={isLoading}
              disabled={isLoading}
              title={t('incoming.list.resume_stream')}
            />
          );
        }
        
        return '-';
      },
    },
  ];

  return (
    <Card 
      title={t('incoming.list.title')}
      extra={
        <Space>
          <Tooltip title={isAutoRefreshing ? t('incoming.list.auto_refresh_on') : t('incoming.list.auto_refresh_off')}>
            <Button
              type={isAutoRefreshing ? 'primary' : 'default'}
              icon={<ReloadOutlined />}
              onClick={toggleAutoRefresh}
              size="small"
            >
              {isAutoRefreshing ? t('incoming.list.auto_refresh_on') : t('incoming.list.auto_refresh_off')}
            </Button>
          </Tooltip>
          {isAutoRefreshing && (
            <Tooltip title={t('incoming.list.next_refresh', { seconds: countdown })}>
              <Progress
                type="circle"
                size={24}
                percent={((5 - countdown) / 5) * 100}
                showInfo={false}
                strokeColor="#1890ff"
                strokeWidth={8}
              />
            </Tooltip>
          )}
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert message={error} type="error" />
      ) : (
        <Table
          columns={columns}
          dataSource={streams}
          rowKey="id"
          pagination={false}
        />
      )}
    </Card>
  );
};

export default IncomingStreamsPage; 